import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChevronDown,
    Calendar,
    Upload,
    Check,
    Building2,
    FileText,
    ArrowRight,
    Info,
} from "lucide-react";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import classes from "./NewReconciliationRun.module.css";

const NewReconciliationRun = () => {
    const navigate = useNavigate();

    const [account, setAccount] = useState("GTBank Main Account");
    const [currency, setCurrency] = useState("NGN - Nigerian Naira");
    const [fromDate, setFromDate] = useState("01 Aug 2026");
    const [toDate, setToDate] = useState("14 Aug 2026");

    const [bankSource, setBankSource] = useState("GTBank Statement");
    const [ledgerSource, setLedgerSource] = useState("General Ledger");
    const [ledgerSet, setLedgerSet] = useState("General Ledger - Aug 2026");

    const [amountTolerance, setAmountTolerance] = useState("1,000.00");
    const [dateTolerance, setDateTolerance] = useState("2 Days");
    const [narrationSimilarity, setNarrationSimilarity] = useState("90%");
    const [confidence, setConfidence] = useState("High (90% and above)");

    const [rules, setRules] = useState({
        reference: true,
        amount: true,
        date: true,
        narration: true,
        customer: true,
        account: false,
    });

    const toggleRule = (rule) => {
        setRules((previous) => ({
            ...previous,
            [rule]: !previous[rule],
        }));
    };

    return (
        <div className={classes.page}>
            <Header />
            <Sidebar />

            <main className={classes.main}>
                <div className={classes.container}>

                    {/* Page Heading */}
                    <div className={classes.pageHeading}>
                        <div>
                            <h1>New Reconciliation Run</h1>
                            <p>
                                Follow the step below to create and start a new reconciliation run
                            </p>
                        </div>
                    </div>

                    {/* Steps */}
                    <div className={classes.steps}>

                        <div className={`${classes.step} ${classes.activeStep}`}>
                            <div className={classes.stepNumber}>1</div>
                            <div className={classes.stepText}>
                                <strong>Select Account</strong>
                                <span>Choose account and period</span>
                            </div>
                        </div>

                        <div className={classes.stepLine}></div>

                        <div className={`${classes.step} ${classes.activeStep}`}>
                            <div className={classes.stepNumber}>2</div>
                            <div className={classes.stepText}>
                                <strong>Select Sources</strong>
                                <span>Upload or choose data</span>
                            </div>
                        </div>

                        <div className={classes.stepLine}></div>

                        <div className={`${classes.step} ${classes.activeStep}`}>
                            <div className={classes.stepNumber}>3</div>
                            <div className={classes.stepText}>
                                <strong>Matching Rules</strong>
                                <span>Configure matching</span>
                            </div>
                        </div>

                        <div className={classes.stepLine}></div>

                        <div className={classes.step}>
                            <div className={classes.stepNumber}>4</div>
                            <div className={classes.stepText}>
                                <strong>Review &amp; Start</strong>
                                <span>Confirm and start run</span>
                            </div>
                        </div>

                    </div>

                    {/* Main Layout */}
                    <div className={classes.contentLayout}>

                        {/* LEFT SIDE */}
                        <div className={classes.leftContent}>

                            {/* Account & Period */}
                            <section className={classes.card}>
                                <div className={classes.sectionTitle}>
                                    <h2>Select Account &amp; Period</h2>
                                </div>

                                <div className={classes.formGridFour}>

                                    <div className={classes.formGroup}>
                                        <label>Bank Account</label>

                                        <div className={classes.selectWrapper}>
                                            <select
                                                value={account}
                                                onChange={(e) =>
                                                    setAccount(e.target.value)
                                                }
                                            >
                                                <option>GTBank Main Account</option>
                                                <option>Fidelity Account</option>
                                                <option>UBA Payments Account</option>
                                                <option>Access Bank Account</option>
                                            </select>

                                            <ChevronDown size={15} />
                                        </div>
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Currency</label>

                                        <div className={classes.selectWrapper}>
                                            <select
                                                value={currency}
                                                onChange={(e) =>
                                                    setCurrency(e.target.value)
                                                }
                                            >
                                                <option>NGN - Nigerian Naira</option>
                                                <option>USD - US Dollar</option>
                                                <option>GBP - British Pound</option>
                                            </select>

                                            <ChevronDown size={15} />
                                        </div>
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>From Date</label>

                                        <div className={classes.dateInput}>
                                            <Calendar size={14} />

                                            <input
                                                type="text"
                                                value={fromDate}
                                                onChange={(e) =>
                                                    setFromDate(e.target.value)
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>To Date</label>

                                        <div className={classes.dateInput}>
                                            <Calendar size={14} />

                                            <input
                                                type="text"
                                                value={toDate}
                                                onChange={(e) =>
                                                    setToDate(e.target.value)
                                                }
                                            />
                                        </div>
                                    </div>

                                </div>
                            </section>

                            {/* Data Sources */}
                            <section className={classes.card}>

                                <div className={classes.sectionTitle}>
                                    <h2>Select Data Sources</h2>
                                </div>

                                <div className={classes.sourceGrid}>

                                    {/* Bank Statement */}
                                    <div className={classes.sourceCard}>

                                        <div className={classes.sourceHeader}>
                                            <span>
                                                Source A (Bank Statement)
                                            </span>
                                        </div>

                                        <div className={classes.formGroup}>
                                            <label>Data Source</label>

                                            <div className={classes.selectWrapper}>
                                                <select
                                                    value={bankSource}
                                                    onChange={(e) =>
                                                        setBankSource(e.target.value)
                                                    }
                                                >
                                                    <option>GTBank Statement</option>
                                                    <option>Fidelity Statement</option>
                                                    <option>UBA Statement</option>
                                                </select>

                                                <ChevronDown size={15} />
                                            </div>
                                        </div>

                                        <div className={classes.formGroup}>
                                            <label>File Upload</label>

                                            <div className={classes.fileBox}>

                                                <div className={classes.fileIcon}>
                                                    <FileText size={18} />
                                                </div>

                                                <div className={classes.fileDetails}>
                                                    <strong>
                                                        GTBank_Statement_Aug_2026
                                                    </strong>

                                                    <span>
                                                        18,420 transactions detected
                                                    </span>
                                                </div>

                                                <button
                                                    className={classes.uploadButton}
                                                    type="button"
                                                >
                                                    <Upload size={16} />
                                                </button>

                                            </div>
                                        </div>

                                        <div className={classes.sourceBottom}>

                                            <span className={classes.successText}>
                                                <Check size={14} />
                                                File validated successfully
                                            </span>

                                            <button
                                                className={classes.previewButton}
                                                type="button"
                                            >
                                                Preview
                                            </button>

                                        </div>

                                    </div>

                                    {/* Ledger */}
                                    <div className={classes.sourceCard}>

                                        <div className={classes.sourceHeader}>
                                            <span>
                                                Source B (Bank Statement)
                                            </span>
                                        </div>

                                        <div className={classes.formGroup}>
                                            <label>Data Source</label>

                                            <div className={classes.selectWrapper}>
                                                <select
                                                    value={ledgerSource}
                                                    onChange={(e) =>
                                                        setLedgerSource(e.target.value)
                                                    }
                                                >
                                                    <option>General Ledger</option>
                                                    <option>Accounting System</option>
                                                    <option>ERP Ledger</option>
                                                </select>

                                                <ChevronDown size={15} />
                                            </div>
                                        </div>

                                        <div className={classes.formGroup}>
                                            <label>Data Set / Account</label>

                                            <div className={classes.selectWrapper}>
                                                <select
                                                    value={ledgerSet}
                                                    onChange={(e) =>
                                                        setLedgerSet(e.target.value)
                                                    }
                                                >
                                                    <option>
                                                        General Ledger - Aug 2026
                                                    </option>

                                                    <option>
                                                        General Ledger - Jul 2026
                                                    </option>
                                                </select>

                                                <ChevronDown size={15} />
                                            </div>
                                        </div>

                                        <div className={classes.detectedBox}>

                                            <div>
                                                <Check size={14} />

                                                <span>
                                                    18,210 transactions detected
                                                </span>
                                            </div>

                                            <button
                                                className={classes.previewButton}
                                                type="button"
                                            >
                                                Preview
                                            </button>

                                        </div>

                                    </div>

                                </div>

                            </section>

                            {/* Matching Rules */}
                            <section className={classes.card}>

                                <div className={classes.sectionTitle}>
                                    <h2>
                                        Matching Rules &amp; Settings
                                    </h2>
                                </div>

                                <div className={classes.rulesLayout}>

                                    <div className={classes.rulesSection}>

                                        <h3>Matching Rules</h3>

                                        <div className={classes.checkboxGrid}>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rules.reference}
                                                    onChange={() =>
                                                        toggleRule("reference")
                                                    }
                                                />
                                                <span>Match by Reference</span>
                                            </label>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rules.narration}
                                                    onChange={() =>
                                                        toggleRule("narration")
                                                    }
                                                />
                                                <span>Match by Narration</span>
                                            </label>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rules.amount}
                                                    onChange={() =>
                                                        toggleRule("amount")
                                                    }
                                                />
                                                <span>Match by Amount</span>
                                            </label>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rules.customer}
                                                    onChange={() =>
                                                        toggleRule("customer")
                                                    }
                                                />
                                                <span>Match by Customer</span>
                                            </label>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rules.date}
                                                    onChange={() =>
                                                        toggleRule("date")
                                                    }
                                                />
                                                <span>Match by Date</span>
                                            </label>

                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={rules.account}
                                                    onChange={() =>
                                                        toggleRule("account")
                                                    }
                                                />
                                                <span>Match by Account</span>
                                            </label>

                                        </div>

                                        <div className={classes.formGroup}>

                                            <label>
                                                Auto Match Confidence
                                            </label>

                                            <div className={classes.selectWrapper}>

                                                <select
                                                    value={confidence}
                                                    onChange={(e) =>
                                                        setConfidence(e.target.value)
                                                    }
                                                >
                                                    <option>
                                                        High (90% and above)
                                                    </option>

                                                    <option>
                                                        Medium (70% and above)
                                                    </option>

                                                    <option>
                                                        Low (50% and above)
                                                    </option>
                                                </select>

                                                <ChevronDown size={15} />

                                            </div>

                                        </div>

                                    </div>

                                    <div className={classes.toleranceSection}>

                                        <h3>Tolerance Settings</h3>

                                        <div className={classes.toleranceGrid}>

                                            <div className={classes.formGroup}>

                                                <label>
                                                    Amount Tolerance
                                                </label>

                                                <div className={classes.inputWithPrefix}>
                                                    <span>₦</span>

                                                    <input
                                                        value={amountTolerance}
                                                        onChange={(e) =>
                                                            setAmountTolerance(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>

                                            </div>

                                            <div className={classes.formGroup}>

                                                <label>
                                                    Date Tolerance
                                                </label>

                                                <div className={classes.selectWrapper}>

                                                    <select
                                                        value={dateTolerance}
                                                        onChange={(e) =>
                                                            setDateTolerance(
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option>2 Days</option>
                                                        <option>1 Day</option>
                                                        <option>3 Days</option>
                                                        <option>5 Days</option>
                                                    </select>

                                                    <ChevronDown size={15} />

                                                </div>

                                            </div>

                                        </div>

                                        <div className={classes.formGroup}>

                                            <label>
                                                Narration Similarity
                                            </label>

                                            <div className={classes.selectWrapper}>

                                                <select
                                                    value={narrationSimilarity}
                                                    onChange={(e) =>
                                                        setNarrationSimilarity(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option>90%</option>
                                                    <option>80%</option>
                                                    <option>70%</option>
                                                </select>

                                                <ChevronDown size={15} />

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </section>

                            {/* Bottom Buttons */}
                            <div className={classes.bottomActions}>

                                <button
                                    className={classes.cancelButton}
                                    onClick={() =>
                                        navigate("/Reconcilation")
                                    }
                                    type="button"
                                >
                                    Cancel
                                </button>

                                <button
                                    className={classes.nextButton}
                                    type="button"
                                >
                                    Next
                                    <ArrowRight size={17} />
                                </button>

                            </div>

                        </div>

                        {/* RIGHT SUMMARY */}
                        <aside className={classes.summary}>

                            <div className={classes.summaryTitle}>
                                <h2>Run Summary</h2>
                            </div>

                            {/* Account */}
                            <div className={classes.summaryItem}>

                                <div className={classes.summaryIcon}>
                                    <Building2 size={17} />
                                </div>

                                <div>
                                    <h3>Account</h3>

                                    <strong>
                                        {account}
                                    </strong>

                                    <span>
                                        GTBank - 1234567890
                                    </span>
                                </div>

                            </div>

                            {/* Period */}
                            <div className={classes.summaryItem}>

                                <div className={classes.summaryIcon}>
                                    <Calendar size={17} />
                                </div>

                                <div>
                                    <h3>Period</h3>

                                    <strong>
                                        {fromDate} - {toDate}
                                    </strong>

                                    <span>
                                        14 days
                                    </span>
                                </div>

                            </div>

                            {/* Sources */}
                            <div className={classes.summarySources}>

                                <h3>Sources</h3>

                                <div className={classes.sourceSummary}>

                                    <div className={classes.summaryIcon}>
                                        <Building2 size={16} />
                                    </div>

                                    <div>

                                        <strong>
                                            {bankSource}
                                        </strong>

                                        <span>
                                            GTBank_Statement_Aug_2026.xlsx
                                        </span>

                                        <span>
                                            18,420 transactions
                                        </span>

                                    </div>

                                </div>

                                <div className={classes.summaryDate}>
                                    <Calendar size={14} />

                                    <span>
                                        {fromDate} - {toDate}
                                    </span>
                                </div>

                            </div>

                            {/* Matching */}
                            <div className={classes.summaryMatching}>

                                <h3>
                                    Matching Configurations
                                </h3>

                                <div className={classes.summaryRow}>
                                    <span>
                                        Rules Selected
                                    </span>

                                    <strong>
                                        {Object.values(rules).filter(Boolean).length} of 6
                                    </strong>
                                </div>

                                <div className={classes.summaryRow}>
                                    <span>
                                        Amount Tolerance
                                    </span>

                                    <strong>
                                        ₦{amountTolerance}
                                    </strong>
                                </div>

                                <div className={classes.summaryRow}>
                                    <span>
                                        Date Tolerance
                                    </span>

                                    <strong>
                                        ± {dateTolerance.toLowerCase()}
                                    </strong>
                                </div>

                                <div className={classes.summaryRow}>
                                    <span>
                                        Auto-Match Confidence
                                    </span>

                                    <strong>
                                        {confidence}
                                    </strong>
                                </div>

                            </div>

                            {/* Transactions */}
                            <div className={classes.transactionSummary}>

                                <h3>
                                    Transactions Summary
                                </h3>

                                <div className={classes.summaryRow}>
                                    <span>
                                        Bank Transactions
                                    </span>

                                    <strong>
                                        18,420
                                    </strong>
                                </div>

                                <div className={classes.summaryRow}>
                                    <span>
                                        Ledger Transactions
                                    </span>

                                    <strong>
                                        18,210
                                    </strong>
                                </div>

                                <div className={classes.totalRow}>
                                    <span>
                                        Total to Reconcile
                                    </span>

                                    <strong>
                                        36,630
                                    </strong>
                                </div>

                            </div>

                            {/* Info */}
                            <div className={classes.nextInfo}>

                                <div className={classes.infoIcon}>
                                    <Info size={15} />
                                </div>

                                <div>

                                    <h3>
                                        What happens next
                                    </h3>

                                    <p>
                                        After you start the run, PENTSUITE will
                                        automatically process and match your
                                        transactions based on the rules you've
                                        configured.
                                    </p>

                                </div>

                            </div>

                        </aside>

                    </div>

                </div>
            </main>
        </div>
    );
};

export default NewReconciliationRun;